import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useAuth } from '../../../../hooks/useAuth';
import { useEntitiesQuery } from '../../../../hooks/queries/useEntitiesQuery';
import { useUpdateEntityMutation } from '../../../../hooks/mutations/useEntityMutation';
import { Entity } from '../../../../services/apiSLTP/sites/entities';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { crudStatus, getCrudStatusInfos, localStorageKeys, permissionLevels } from '../../../../utils/enums';
import { getStatusColor } from '../../../../utils/colors';
import TableOptionBar from '../../TableOptionBar';
import Icon from '../../../Icon';
import StateCell from '../../../cells/StateCell';
import IconLine from '../../../IconLine';
import UpdateEntityModal from '../../../modals/admin/internal/UpdateEntityModal';
import DeleteModal from '../../../modals/DeleteModal';
import GenericAgGridTable from '../../GenericAgGridTable';
import { formatNumber } from '../../../../utils/numbers';
import IconButton from '../../../shared/IconButton';
import { createDateTimeColumn } from '../../../../utils/dates';

const EntitiesSettingsRow: React.FC<{ withUsageCount: boolean }> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: entities, isLoading, refetch } = useEntitiesQuery({ queries: { withUsage: withUsageCount } });

    const { mutate } = useUpdateEntityMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating entity:', error.message),
    });

    const handleEdit = (entity: Entity) => {
        setSelectedEntity(entity);
        setIsEditMode(true);
        setShowEditModal(true);
    };

    const handleDelete = (entity: Entity) => {
        setSelectedEntity(entity);
        setShowDeleteModal(true);
    };

    const columnDefs: ColDef<Entity>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },

        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Entity>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyEntity'))}
                            onClick={() => {
                                if (!params.data) return;
                                handleEdit(params.data);
                            }}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteEntity'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    handleDelete(params.data);
                                }}
                                disabled={params.data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Entity>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('address')),
            field: 'address',
            colId: 'address',
            initialWidth: 300,
        },
        {
            headerName: capitalizeFirstLetter(t('latitude')),
            field: 'latitude',
            colId: 'latitude',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 8),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('longitude')),
            field: 'longitude',
            colId: 'longitude',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 8),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        createDateTimeColumn<Entity>('createdAt', 'createdAt'),
        createDateTimeColumn<Entity>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
                colId: '_id',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xtotal',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xSites')),
                field: 'usageCount.sites',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xsites',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={entities || []}
                loading={isLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedEntity(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addEntity'))}
                    />
                }
                exportCsvFileName="admin-entities"
                storageKey={localStorageKeys.TABLE.ADMIN.INTERNAL.ENTITIES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateEntityModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    entity={selectedEntity}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && selectedEntity && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutate({ entityId: selectedEntity._id, data: { crudStatus: crudStatus.DELETED } });
                    }}
                    title={t('entity')}
                    itemName={selectedEntity.designation}
                />
            )}
        </>
    );
};

export default EntitiesSettingsRow;
